import { Injectable } from '@angular/core';

type ExternalScriptOptions = {
  id?: string;
  async?: boolean;
  defer?: boolean;
  dataAttributes?: Record<string, string>;
};

@Injectable({ providedIn: 'root' })
export class ExternalScriptLoaderService {
  private readonly allowedHosts = new Set([
    'unpkg.com',
    'accounts.google.com',
    'www.paypal.com',
    'maps.googleapis.com',
  ]);
  private readonly loadPromises = new Map<string, Promise<void>>();

  load(url: string, options: ExternalScriptOptions = {}): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.resolve();
    }

    const normalizedUrl = this.validateUrl(url);
    const key = options.id || normalizedUrl;
    const cached = this.loadPromises.get(key);
    if (cached) {
      return cached;
    }

    const existing = this.findExistingScript(options.id, normalizedUrl);
    if (existing) {
      const existingPromise = this.waitForLoad(existing);
      this.loadPromises.set(key, existingPromise);
      return existingPromise;
    }

    const script = document.createElement('script');
    if (options.id) {
      script.id = options.id;
    }
    script.src = normalizedUrl;
    script.async = options.async ?? true;
    script.defer = options.defer ?? false;

    for (const [keyName, value] of Object.entries(options.dataAttributes || {})) {
      script.setAttribute(`data-${keyName}`, value);
    }

    const promise = this.waitForLoad(script);
    this.loadPromises.set(key, promise);
    document.head.appendChild(script);
    return promise;
  }

  private waitForLoad(script: HTMLScriptElement): Promise<void> {
    return new Promise((resolve, reject) => {
      const loaded = script.getAttribute('data-loaded') === 'true';
      if (loaded) {
        resolve();
        return;
      }

      const onLoad = () => {
        script.setAttribute('data-loaded', 'true');
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error(`Failed to load script: ${script.src}`));
      };
      let timeoutId: any = null;
      const cleanup = () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      // Fallback for previously inserted scripts that are already loaded but did not dispatch "load" to this listener.
      timeoutId = setTimeout(() => {
        script.setAttribute('data-loaded', 'true');
        cleanup();
        resolve();
      }, 2000);
    });
  }

  private findExistingScript(id: string | undefined, src: string): HTMLScriptElement | null {
    if (id) {
      const byId = document.getElementById(id) as HTMLScriptElement | null;
      if (byId) {
        return byId;
      }
    }
    return document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
  }

  private validateUrl(url: string): string {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      throw new Error(`Invalid script URL: ${url}`);
    }
    if (parsed.protocol !== 'https:') {
      throw new Error(`Only HTTPS scripts are allowed: ${url}`);
    }
    if (!this.allowedHosts.has(parsed.hostname)) {
      throw new Error(`Host not allowed for external script: ${parsed.hostname}`);
    }
    return parsed.toString();
  }
}
